import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Heart, Star, MessageSquare, Camera, Phone, Video, Contact, MapPin, Briefcase, GraduationCap,
  Users, Sparkles, ShieldAlert, Ban, StickyNote, Share2, Loader2, ArrowLeft, Check,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Avatar, photoSrc } from "@/components/Avatar";
import { ProtectedMedia } from "@/components/ProtectedMedia";
import { VerifiedBadges } from "@/components/VerifiedBadges";
import { CompatibilityRing, CompatibilityBreakdown } from "@/components/Compatibility";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";

export default function ProfileView() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [why, setWhy] = useState(null);
  const [whyBusy, setWhyBusy] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/profile/${userId}`).then(({ data }) => { setD(data); }).catch((e) => { toast.error(apiError(e)); nav("/app/matches"); });
  useEffect(() => { load(); api.get(`/notes/${userId}`).then(({ data }) => setNote(data.note)).catch(() => {}); /* eslint-disable-next-line */ }, [userId]);

  // Screenshot deterrent (best-effort) + block right-click on member photos
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "PrintScreen") {
        try { navigator.clipboard.writeText(""); } catch (_) {}
        toast.warning("Member photos are watermarked & protected. Please respect privacy.");
      }
    };
    const blockCtx = (e) => { if (e.target && e.target.tagName === "IMG") e.preventDefault(); };
    window.addEventListener("keyup", onKey);
    document.addEventListener("contextmenu", blockCtx);
    return () => { window.removeEventListener("keyup", onKey); document.removeEventListener("contextmenu", blockCtx); };
  }, []);

  if (!d) return <FullSpinner />;
  const p = d.profile;
  const comp = p.compatibility;

  const act = async (fn, msg) => { setBusy(true); try { await fn(); if (msg) toast.success(msg); await load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const sendInterest = () => act(() => api.post("/interests/send", { to_user: userId }), "Interest sent");
  const shortlist = () => act(() => api.post(`/shortlist/${userId}`), "Updated shortlist");
  const request = (kind) => act(() => api.post(`/requests/${kind}/${userId}`), `${kind} request sent`);
  const callRequest = (kind) => act(async () => { await api.post("/calls/request", { to_user: userId, kind }); nav("/app/calls"); }, `${kind} call requested`);
  const block = () => act(() => api.post(`/block/${userId}`), "User blocked").then(() => nav("/app/matches"));
  const saveNote = async () => { try { await api.put(`/notes/${userId}`, { text: note }); toast.success("Private note saved"); } catch (e) { toast.error(apiError(e)); } };

  const loadWhy = async () => {
    setWhyBusy(true);
    try { const { data } = await api.get(`/match/why/${userId}`); setWhy(data); } catch (e) { toast.error(apiError(e)); } finally { setWhyBusy(false); }
  };

  const share = async () => {
    const url = `${window.location.origin}/p/${p.profile_code}`;
    try { await navigator.clipboard.writeText(url); toast.success("Shareable link copied"); } catch { toast.info(url); }
  };

  const report = () => {
    const reason = window.prompt("Report reason (Fake Profile / Scam / Harassment / Spam / Other):", "Fake Profile");
    if (!reason) return;
    api.post("/report", { target_user: userId, reason }).then(() => toast.success("Report submitted confidentially")).catch((e) => toast.error(apiError(e)));
  };

  const gallery = p.photos || [];

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="profile-view-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#605255] flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left: photo + actions */}
        <div className="lg:col-span-1 space-y-4">
          <div className="mj-card overflow-hidden">
            <div className="aspect-[4/5] bg-slate-100">
              <ProtectedMedia className="w-full h-full">
                <Avatar photos={gallery} name={p.full_name} size={9999} className="w-full h-full" />
              </ProtectedMedia>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h1 className="font-serif text-2xl font-bold text-[#261A1C]">{p.full_name}{p.age ? `, ${p.age}` : ""}</h1>
                <span className="text-xs font-mono text-[#9E7B1B]">{p.profile_code}</span>
              </div>
              {comp && (
                <div className="mt-3 flex items-center gap-3 bg-[#FAF5EE] rounded-xl p-3">
                  <CompatibilityRing score={comp.score} />
                  <div><div className="text-sm font-semibold text-[#781826]">{comp.score}% Compatible</div><div className="text-xs text-[#605255]">Informational compatibility</div></div>
                </div>
              )}
            </div>
          </div>

          <div className="mj-card p-4 grid grid-cols-2 gap-2">
            <button data-testid="pv-interest" disabled={busy || d.my_interest?.status === "pending" || d.connected} onClick={sendInterest} className="btn-primary col-span-2 disabled:opacity-60">
              <Heart className="w-4 h-4" /> {d.connected ? "Connected" : d.my_interest?.status === "pending" ? "Interest Sent" : "Send Interest"}
            </button>
            <button data-testid="pv-shortlist" onClick={shortlist} disabled={busy} className="btn-outline text-sm"><Star className="w-4 h-4" fill={d.shortlisted ? "#C59B27" : "none"} /> Shortlist</button>
            <button data-testid="pv-chat" onClick={() => nav(`/app/messages/${userId}`)} className="btn-outline text-sm"><MessageSquare className="w-4 h-4" /> Chat</button>
            <button data-testid="pv-photo" onClick={() => request("photo")} disabled={busy} className="btn-outline text-sm"><Camera className="w-4 h-4" /> Photo</button>
            <button data-testid="pv-contact" onClick={() => request("contact")} disabled={busy} className="btn-outline text-sm"><Contact className="w-4 h-4" /> Contact</button>
            <button data-testid="pv-voice" onClick={() => callRequest("voice")} disabled={busy} className="btn-outline text-sm"><Phone className="w-4 h-4" /> Voice</button>
            <button data-testid="pv-video" onClick={() => callRequest("video")} disabled={busy} className="btn-outline text-sm"><Video className="w-4 h-4" /> Video</button>
            <button data-testid="pv-share" onClick={share} className="btn-outline text-sm col-span-2"><Share2 className="w-4 h-4" /> Share Profile</button>
          </div>

          <div className="mj-card p-4 flex gap-2">
            <button data-testid="pv-report" onClick={report} className="flex-1 text-sm text-[#B45309] flex items-center justify-center gap-1 py-2 rounded-lg hover:bg-amber-50"><ShieldAlert className="w-4 h-4" /> Report</button>
            <button data-testid="pv-block" onClick={block} className="flex-1 text-sm text-[#BE123C] flex items-center justify-center gap-1 py-2 rounded-lg hover:bg-red-50"><Ban className="w-4 h-4" /> Block</button>
          </div>
        </div>

        {/* Right: details */}
        <div className="lg:col-span-2 space-y-4">
          <div className="mj-card p-5">
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#261A1C] mb-3">
              {p.city && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#C59B27]" /> {[p.city, p.state].filter(Boolean).join(", ")}</span>}
              {p.occupation && <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-[#C59B27]" /> {p.occupation}</span>}
              {p.qualification && <span className="flex items-center gap-1.5"><GraduationCap className="w-4 h-4 text-[#C59B27]" /> {p.qualification}</span>}
            </div>
            <VerifiedBadges verification={p.verification} />
            {p.about_me && <p className="text-sm text-[#605255] mt-4 leading-relaxed">{p.about_me}</p>}
          </div>

          {/* Match passport / Why */}
          <div className="mj-card p-5" data-testid="match-passport">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif text-xl font-semibold text-[#261A1C] flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#C59B27]" /> Match Passport</h2>
              {!why && <button onClick={loadWhy} disabled={whyBusy} data-testid="why-match-btn" className="btn-outline text-xs">{whyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Why this match?"}</button>}
            </div>
            {comp && <CompatibilityBreakdown breakdown={comp.breakdown} />}
            {why && (
              <div className="mt-4 bg-[#FAF5EE] rounded-xl p-4">
                <div className="text-xs font-semibold text-[#C59B27] uppercase tracking-wide mb-1">Why ManaJodi AI recommends this</div>
                <p className="text-sm text-[#261A1C]">{why.explanation}</p>
                <p className="text-[11px] text-[#9E7B1B] mt-2">Informational only — not a prediction of relationship success.</p>
              </div>
            )}
          </div>

          <DetailGrid title="Personal" items={[["Date of Birth", p.dob], ["Height", p.height], ["Marital Status", p.marital_status], ["Mother Tongue", p.mother_tongue], ["Religion", p.religion], ["Community", p.community], ["Residency", p.residency_status]]} />
          <DetailGrid title="Education & Career" items={[["Qualification", p.qualification], ["Specialization", p.specialization], ["Occupation", p.occupation], ["Industry", p.industry], ["Company", p.company], ["Income", p.income_range]]} />
          <DetailGrid title="Lifestyle" items={[["Diet", p.diet], ["Smoking", p.smoking], ["Drinking", p.drinking], ["Fitness", p.fitness], ["Hobbies", (p.hobbies || []).join(", ")], ["Interests", (p.interests || []).join(", ")]]} />
          <DetailGrid title="Family" items={[["Father", p.father_occupation], ["Mother", p.mother_occupation], ["Family Type", p.family_type], ["Family Values", p.family_values], ["Family Location", p.family_location]]} icon={Users} />
          {(p.mobile || p.email) && (
            <div className="mj-card p-5">
              <h2 className="font-serif text-lg font-semibold text-[#261A1C] mb-2">Contact <span className="text-xs text-emerald-600">(shared with you)</span></h2>
              <div className="text-sm text-[#261A1C] space-y-1">{p.mobile && <div>📱 {p.mobile}</div>}{p.email && <div>✉️ {p.email}</div>}</div>
            </div>
          )}

          {/* Private note */}
          <div className="mj-card p-5">
            <h2 className="font-serif text-lg font-semibold text-[#261A1C] flex items-center gap-2 mb-2"><StickyNote className="w-4 h-4 text-[#C59B27]" /> Private Note</h2>
            <p className="text-xs text-[#9E7B1B] mb-2">Only you can see this. It is never shared with the profile owner.</p>
            <textarea data-testid="private-note" className="mj-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Spoke with family on Sept 28…" />
            <button onClick={saveNote} data-testid="save-note-btn" className="btn-outline text-sm mt-2"><Check className="w-4 h-4" /> Save Note</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailGrid({ title, items, icon: Icon }) {
  const filled = items.filter(([, v]) => v);
  if (filled.length === 0) return null;
  return (
    <div className="mj-card p-5">
      <h2 className="font-serif text-lg font-semibold text-[#261A1C] mb-3 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-[#C59B27]" />}{title}</h2>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
        {filled.map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm border-b border-[#F1E9DA] pb-1.5">
            <span className="text-[#605255]">{k}</span><span className="text-[#261A1C] font-medium text-right">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
